import { format, addDays, differenceInDays } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { Options, RRule, RRuleSet, rrulestr, Frequency } from 'rrule';
import { shiftFreqOption } from '../types';

type Weekday = {
  readonly text: string;
  readonly value: number;
};

const TIMEZONE = 'Australia/Sydney';

export const FreqTextMap = {
  [shiftFreqOption.Values.MONTHLY]: 'months',
  [shiftFreqOption.Values.WEEKLY]: 'weeks',
  [shiftFreqOption.Values.DAILY]: 'days',
  [shiftFreqOption.Values.CUSTOM]: 'days',
};

export const FreqOptionMap = {
  [shiftFreqOption.Values.MONTHLY]: Frequency.MONTHLY,
  [shiftFreqOption.Values.WEEKLY]: Frequency.WEEKLY,
  [shiftFreqOption.Values.DAILY]: Frequency.DAILY,
  [shiftFreqOption.Values.CUSTOM]: Frequency.DAILY,
};

export const mapRRuleSetFreq = (rruleSet: RRuleSet) => {
  const rrules: readonly RRule[] = rruleSet.rrules();
  if (rrules.length > 1) {
    return shiftFreqOption.Values.CUSTOM;
  } else if (rrules[0] === undefined) {
    return shiftFreqOption.Values.DAILY;
  } else {
    const frequency = rrules[0].options.freq;

    switch (frequency) {
      case Frequency.MONTHLY:
        return shiftFreqOption.Values.MONTHLY;
      case Frequency.WEEKLY:
        return shiftFreqOption.Values.WEEKLY;
      case Frequency.DAILY:
      default:
        return shiftFreqOption.Values.DAILY;
    }
  }
};

/**
 * Map the date to RRule weekday number (e.g. 0 for Monday, 1 for Tuesday etc.).
 * @param currentDate the date we want to mapped.
 * @returns RRule weekday number, return -1 for invalid value.
 */
export const mapToRRuleWeekdayNumber = (currentDate?: Date): number => {
  const weekday = format(currentDate ?? new Date(), 'EEE');
  switch (weekday) {
    case 'Mon':
      return RRule.MO.weekday;
    case 'Tue':
      return RRule.TU.weekday;
    case 'Wed':
      return RRule.WE.weekday;
    case 'Thu':
      return RRule.TH.weekday;
    case 'Fri':
      return RRule.FR.weekday;
    case 'Sat':
      return RRule.SA.weekday;
    case 'Sun':
      return RRule.SU.weekday;
    default:
      return -1;
  }
};

/**
 * Weekdays array with text and corresponding RRule weekday number.
 */
export const weekdays: readonly Weekday[] = [
  {
    text: 'Mon',
    value: RRule.MO.weekday,
  },
  {
    text: 'Tue',
    value: RRule.TU.weekday,
  },
  {
    text: 'Wed',
    value: RRule.WE.weekday,
  },
  {
    text: 'Thu',
    value: RRule.TH.weekday,
  },
  {
    text: 'Fri',
    value: RRule.FR.weekday,
  },
  {
    text: 'Sat',
    value: RRule.SA.weekday,
  },
  {
    text: 'Sun',
    value: RRule.SU.weekday,
  },
];

/**
 * Currently we add the time zone to RRule for correct weekday calculation.
 * But from RRule repo, adding the time zone will cause the RRule
 * generated recurrent pattern text add '(~ approximate)' to the end.
 * So we need to remove the '(~ approximate)' manually from the text.
 * Repo reference:
 * @see https://github.com/jakubroztocil/rrule/blob/d352fb3c235fa91f9ee108851f49a4b76e2b8ee4/src/nlp/totext.ts#L135
 * @param recurPatternText RRule generated recurrent pattern text.
 * @returns text with out '(~ approximate)'.
 */
export const removeApproximateFromRecurrentPatternText = (
  recurPatternText?: string
) =>
  recurPatternText === undefined
    ? ''
    : recurPatternText.replace('(~ approximate)', '');

/**
 * Convert the date to the Australia/Sydney timezone and then convert it to UTC
 * without shifting the time. This is how floating dates work in RRule.
 *
 * @param date not in UTC time zone date (AET in our case).
 * @returns UTC time zone date.
 */
export const rRuleDateFromLocalDate = (date?: Date | null) => {
  if (date !== undefined && date !== null) {
    return new Date(
      `${formatInTimeZone(date, TIMEZONE, 'yyyy-MM-dd')}T${formatInTimeZone(
        date,
        TIMEZONE,
        'HH:mm:ss'
      )}Z`
    );
  }

  return undefined;
};

/**
 * Reverse the conversion from "RRule floating date" to the Australia/Sydney
 * timezone.
 *
 * @param date An RRule datetime, presented as a having a `Z` (UTC) offset but
 *             actually is the Australia/Sydney time
 */
export const localDateFromRRuleDate = (date: Date) =>
  zonedTimeToUtc(date.toISOString().replace(/Z$/u, ''), TIMEZONE);

/**
 * Create a new Recurrence Rule, handling conversions of the dates to floating
 * date formats as required by the rrule.js package.
 * We just need following params for RRule to simplify the recurrent rule.
 *
 * @param Options - List of options for the new RRule
 */
export const newRecurRule = ({
  freq,
  interval,
  dtstart,
  byweekday,
  until,
}: Partial<Options>) =>
  new RRule({
    freq: freq,
    interval: interval,
    dtstart: rRuleDateFromLocalDate(dtstart),
    until: rRuleDateFromLocalDate(until),
    byweekday:
      freq === RRule.WEEKLY && byweekday !== null && byweekday !== undefined
        ? byweekday
        : null,
  });

/**
 * Create a new RuleSet that contains one RRule.
 *
 * @param Options - List of options for the new RRule.
 */
export const newRecurRuleSet = (options: Partial<Options>) => {
  const rruleSet = new RRuleSet();
  rruleSet.rrule(newRecurRule(options));

  return rruleSet;
};

/**
 * Create a new RuleSet that contains multiple daily recurrent pattern
 * that can represents any recurrent pattern in a given period.
 *
 * @param Options - List of options for the new RRule.
 * @param customDays - Selected days for custom recurrent pattern.
 */
export const newCustomRecurRuleSet = (
  options: Partial<Options>,
  customDays: readonly number[]
) => {
  const rruleSet = new RRuleSet();
  customDays.forEach((day) => {
    // Only add rrule that the selected day number is less than interval.
    if (options.interval !== undefined && day < options.interval) {
      rruleSet.rrule(
        newRecurRule({
          ...options,
          dtstart:
            options.dtstart !== null && options.dtstart !== undefined
              ? addDays(options.dtstart, day)
              : options.dtstart,
        })
      );
    }
  });

  return rruleSet;
};

/**
 * Parse RRuleSet from string and sort the rrules.
 * Since the default parser rrulestr from rrule package has bug on reading RRuleSet string,
 * we create this custom parser for parsing the RRuleSet.
 * @see https://github.com/jkbrzt/rrule/issues/332#issuecomment-775778753
 * Note: Currently we only parse the RRule in RRuleSet (exclude RDate, EXRule, EXDate).
 * @param rruleSetString -  RRuleSet string.
 * @returns RRuleSet object.
 */
export const parseRRuleSet = (rruleSetString: string): RRuleSet =>
  rruleSetString
    .trim()
    .split(/\s+(?=DTSTART)/u)
    .map((rruleString) => rrulestr(rruleString))
    .sort((r1, r2) => differenceInDays(r1.options.dtstart, r2.options.dtstart))
    .reduce<RRuleSet>((rruleSet, rrule) => {
      rruleSet.rrule(rrule);
      return rruleSet;
    }, new RRuleSet());

/**
 * Map the selected days repeat pattern for RRuleSet.
 * @param rruleSet  - target RRuleSet.
 * @returns selected nth repeat pattern for input RRuleSet.
 */
export const mapCustomRecurDay = (
  rruleSet: RRuleSet,
  startTime?: Date
): readonly number[] => {
  const sortedRRules: readonly RRule[] = rruleSet
    .rrules()
    .toSorted((r1, r2) =>
      differenceInDays(r1.options.dtstart, r2.options.dtstart)
    );

  const firstRRule = sortedRRules[0];
  if (firstRRule !== undefined && startTime !== undefined) {
    return sortedRRules.map((rrule) => {
      const gapDay =
        differenceInDays(
          localDateFromRRuleDate(rrule.options.dtstart),
          startTime
        ) % firstRRule.options.interval;

      // Add interval to day days if the gap is negative.
      return gapDay < 0 ? gapDay + firstRRule.options.interval : gapDay;
    });
  }

  return [0];
};
